import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy, DestroyRef, TemplateRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridRowsSelectedEvent,
} from '@inavista/components/grid';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { ApiResponse } from 'projects/service/src/app/shared/models/app.model';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { IscService } from '../../../services/isc.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { CART_ACTION_ICONS, CART_COLUMNS, DELETE_ITEM_ACTION, buildCartGridConfig } from './parts-catalog-popup.grid.config';

// The old grid's content was window.innerHeight - 500 tall, under its header (it had no toolbar). The inavista grid
// draws its toolbar, header and horizontal scrollbar inside its own box: same rows area as before.
const GRID_CHROME_PX = 92;

@Component({
    selector: 'app-parts-catalog-popup',
    templateUrl: './parts-catalog-popup.component.html',
    styleUrls: ['./parts-catalog-popup.component.scss'],
    // The grid reads this.data (GetUserCartItems), not an endpoint.
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartsCatalogPopupComponent implements OnInit {
  isExpanded: boolean = true;
  @Output() onClose = new EventEmitter();
  @Output() onExpandCollapse = new EventEmitter();
  @ViewChild('cartGrid') grid: InavistaGrid;
  @ViewChild('quantityCell', { static: true }) quantityCell: TemplateRef<unknown>;
  @Input() currency;
  @Input() materials = [];
  @Input() onsiteAddress;
  @Input() serviceOrderId: string = '';
  data: any;
  gridConfig: GridConfig = null;
  readonly actionIcons = CART_ACTION_ICONS;
  showCartCheckoutPopup: boolean = false;
  showLoader: boolean = false;
  gridHeight: number = window.innerHeight - 500 + GRID_CHROME_PX;
  hidePopup: boolean = false;
  isCheckoutButtonDisabled: boolean = true;
  userDetails;
  stats: any = [];
  totalQuantity = 0;
  totalNetPrice = 0;
  totalPrice = 0;
  totalSavings = 0;
  selectedItemsToCheckout: any;
  onQtyUpdate = new Subject<any>();
  // The ticked rows, by id.
  private checkedIds: unknown[] = [];
  // The old grid ticked every row it showed once its data was bound: set when new rows are handed to the grid.
  private tickAllOnLoad = false;
  private readonly gridRows = inject(LocalRowsGridApi);
  private readonly destroyRef = inject(DestroyRef);

  constructor(private commonService: CommonService, private iscService: IscService, private dialog: MatDialog) { }

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode && !eqData?.item) return;
    const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  ngOnInit(): void {
    this.gridRows.columns.set(LocalRowsGridApi.schema(CART_COLUMNS));
    this.gridConfig = buildCartGridConfig({
      stockOf: row => (this.userDetails?.orderCurrency === 'USD' ? row['stock'] : row['stockCan']),
      quantityTemplate: this.quantityCell,
    });
    !this.materials?.length && this.loadPage();
    this.setData(this.materials);
    this.onQtyUpdate
      .pipe(
        debounceTime(500),
        // Fixed (Martin, 2026-10-28): the old check compared the row object itself, always the same for one row, so
        // a second change of the same row's quantity was dropped (price not refreshed, cart not saved). Compared
        // by row and quantity now: every new quantity is priced and saved, as the first one always was.
        distinctUntilChanged((a: any, b: any) => a.id === b.id && a.quantity === b.quantity),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((data: any) => {
        this.showLoader = true;
        this.iscService.refreshPrice(data.item, data.quantity).subscribe({
          next: (res: ApiResponse) => {
            if (res.isSuccessful && res.result) {
              // The row as it is now (it may have been deleted or reloaded meanwhile).
              const rowData = this.rowById(data.id);
              if (rowData) {
                rowData.netPrice = res?.result?.result?.netAmount?.netAmountValue;
                rowData.itemDiscount = res?.result?.result?.discountAmount?.discountAmountValue;
                rowData.grossAmount = res?.result?.result?.grossAmount?.grossAmountValue;
                this.pushRows(true);
              }
              this.updateTotals();
              this.commonService.showNotification('success', res.message);
              if (rowData) {
                this.iscService.updateUserCart(rowData).subscribe({
                  next: (res: ApiResponse) => {
                    this.commonService.showNotification('success', res.message);
                  },
                  error: (err: Error) => {
                    this.commonService.showNotification('success', err.message);
                  }
                })
              }
            } else {
              this.commonService.showNotification('error', res.message);
            }
            this.showLoader = false;
          },
          error: (err: Error) => {
            this.commonService.showNotification('error', err.message);
            this.showLoader = false;
          }
        })
      });
  }

  loadPage() {
    this.showLoader = true;
    this.userDetails = this.commonService.USER;
    this.iscService.getUserCartItem().subscribe((res: any) => {
      res.result.map((row, index) => {
        row.lineNumber = index + 1;
        row.isSelected = false;
      });
      this.setData(res?.result);
      this.stats = this.data;
      this.showLoader = false;
    })
  }

  /** New cart rows: shown, then all ticked (as the old grid's dataBound did). */
  private setData(rows: any[]) {
    this.data = rows;
    this.tickAllOnLoad = true;
    this.pushRows(false);
  }

  /** Hands this.data to the grid. The old grid's Query showed only the rows with isDeleted = false. */
  private pushRows(keepScroll: boolean) {
    this.gridRows.rows.set((this.data ?? []).filter(row => row.isDeleted === false).map(row => ({ ...row })));
    this.grid?.refresh(keepScroll ? { keepScroll: true } : undefined);
  }

  // The grid has (re)loaded its rows: tick the ones it shows, once per new set of rows.
  onActionComplete() {
    if (!this.tickAllOnLoad || !this.grid) return;
    const ids = this.grid.visibleRows().map(row => row['id']);
    // The grid's own first (empty) load can complete after the cart rows were handed over: wait for the rows.
    if (!ids.length && this.gridRows.rows().length) return;
    this.tickAllOnLoad = false;
    this.grid.setCheckedKeys(ids);
  }

  private rowById(id: unknown): any {
    return (this.data ?? []).find(row => row.id === id);
  }

  private checkedRows(): any[] {
    return (this.data ?? []).filter(row => this.checkedIds.includes(row.id));
  }

  // The totals under the grid: the ticked rows' quantities, discounts and net prices.
  private updateTotals() {
    let totalDiscount = 0;
    let totalPrice = 0;
    let totalQuantity = 0;
    for (const r of this.checkedRows()) {
      totalQuantity += r.quantity;
      totalDiscount += r.itemDiscount;
      totalPrice += r.netPrice;
    }
    this.totalQuantity = totalQuantity;
    this.totalSavings = totalDiscount;
    this.totalPrice = totalPrice;
  }

  // The old Order Qty box: whole numbers, at least 1 (an emptied box went back to 1).
  onQuantityInput(event: Event, row: any) {
    const input = event.target as HTMLInputElement;
    const typed = Math.round(Number(input.value));
    const quantity = input.value === '' || !(typed >= 1) ? 1 : typed;
    input.value = String(quantity);
    this.setQuantity(row, quantity);
  }

  /** A new quantity for a row: totals now, then (debounced) the price is refreshed and the cart saved. */
  private setQuantity(row: any, quantity: number) {
    const rowData = this.rowById(row.id);
    if (!rowData || rowData.quantity === quantity) return;
    rowData.quantity = quantity;
    this.pushRows(true);
    this.updateTotals();
    this.onQtyUpdate.next({ ...rowData });
  }

  onRowsSelected(event: GridRowsSelectedEvent) {
    this.checkedIds = event.rows.map(row => row['id']);
    for (const row of this.data ?? []) {
      row.isSelected = this.checkedIds.includes(row.id);
    }
    this.onCheckboxChange();
    this.updateTotals();
  }

  onCheckboxChange() {
    const selectedRecords = this.checkedRows().filter((x: any) => !x.isDeleted);
    this.isCheckoutButtonDisabled = !selectedRecords.length;
  }

  resetStatsToZero() {
    this.totalQuantity = 0;
    this.totalNetPrice = 0;
    this.totalPrice = 0;
    this.totalSavings = 0;
  }

  checkAvailability() {
    this.showLoader = true;
    if (this.data === null) {
      this.commonService.showNotification('warning', 'No data present at the moment!');
      this.showLoader = false;
    }
    else {
      this.iscService.checkAvailability(this.data).subscribe((res: ApiResponse) => {
        if (res.isSuccessful) {
          // Fixed (Martin, 2026-10-28): the old grid showed the refreshed rows but kept sending, deleting and
          // clearing from the previous list. One list now: the rows on screen are the rows the page works on.
          this.setData(res.result);
          this.showLoader = false;
        }
        else {
          this.commonService.showNotification('warning', res.message);
          this.showLoader = false;
        }
      })
    }
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    this.showMasterItemCodePopup(this.rowById(event.row['id']) ?? event.row);
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey !== DELETE_ITEM_ACTION) return;
    const rowData = this.rowById(event.row['id']);
    if (!rowData) return;
    this.showLoader = true;
    this.commonService.showConfirmation('Are you sure want to delete this item from cart?')
      .then(result => {
        if (result.isConfirmed) {
          rowData.isDeleted = true;
          this.iscService.deleteItemFromCart(rowData).subscribe((response: ApiResponse) => { // delete an item from cart
            if (response.isSuccessful) {
              const rowIndex = this.data.findIndex(item => item.id === rowData.id);
              this.data.splice(rowIndex, 1);
              this.checkedIds = this.checkedIds.filter(id => id !== rowData.id);
              this.onCheckboxChange();
              this.resetStatsToZero();
              this.loadPage();
              this.showLoader = false;
            }
            else {
              // Fixed (Martin, 2026-10-28): the item is still in the cart, so it is no longer marked deleted here
              // (the old grid left it flagged, and a later checkout sent it with isDeleted = true).
              rowData.isDeleted = false;
              this.commonService.showNotification('warning', 'Item ' + rowData.item + ' cannot deleted');
              this.showLoader = false;
            }
          });
        }
        else {
          this.showLoader = false;
          return;
        }
      });
  }

  clearCart() {
    if (this.data.length) {
      this.commonService.showConfirmation('This will empty your cart !')
        .then(result => {
          if (result.isConfirmed) {
            this.showLoader = true;
            this.iscService.clearCart(this.userDetails.employeeId).subscribe((res: ApiResponse) => {
              this.serviceOrderId && this.iscService.resetCartCount(true);
              this.setData([]);
              this.showLoader = false;
            });
          }
        });
    } else {
      this.commonService.showNotification('info', 'Your cart is empty !');
    }
  }

  add = (row) => {
    const rowData = row && this.rowById(row.id);
    if (rowData) {
      this.setQuantity(rowData, rowData.quantity + 1);
    }
  }

  sub = (row) => {
    const rowData = row && this.rowById(row.id);
    if (rowData) {
      if (rowData.quantity > 1) {
        this.setQuantity(rowData, rowData.quantity - 1);
      }
      else {
        this.commonService.showNotification('warning', 'Quantity cannot be less than 1');
      }
    }
  }

  onCloseCart() {
    this.commonService.showConfirmation('Are you sure want to close cart?')
      .then(result => {
        if (result.isConfirmed) {
          this.serviceOrderId && this.iscService.resetCartCount(true);
          this.onClose.emit();
          this.showCartCheckoutPopup = false;
        }
      });
  }

  onCloseFromCheckout() {
    this.onClose.emit();
    this.showCartCheckoutPopup = false;
  }

  expandCollapse() {
    this.isExpanded = !this.isExpanded;
    this.onExpandCollapse.emit(this.isExpanded);
  }

  openCheckout() {
    this.selectedItemsToCheckout = this.checkedRows();
    this.commonService.showConfirmation('Proceed to checkout?') //Are you sure to checkout all selected items? and
      .then(result => {
        this.showLoader = true;
        if (result.isConfirmed) {
          this.showCartCheckoutPopup = true;
          this.showLoader = false;
          this.hidePopup = true;
          // this.onClose.emit();
        }
        else {
          this.showLoader = false;
          return;
        }
      });
  }
}
